import { useEffect, useState, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../features/auth/AuthContext';
import { fetchMe } from '../../lib/identityRegistry';
import { AdminSidebar } from './AdminSidebar';
import { AdminSidebarOpener } from './AdminSidebarOpener';

interface AdminShellProps extends PropsWithChildren {
  title?: string;
  subtitle?: string;
}

const SIDEBAR_HIDDEN_KEY = 'sense.admin.sidebarHidden';

export function AdminShell({ children, title, subtitle }: AdminShellProps): JSX.Element {
  const navigate = useNavigate();
  const { logout, session } = useAuth();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const permissions = meQuery.data?.permissions ?? [];
  const [sidebarHidden, setSidebarHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_HIDDEN_KEY, sidebarHidden ? '1' : '0');
    } catch {
      // ignore storage errors (private mode, disabled storage)
    }
  }, [sidebarHidden]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="admin-shell">
      {sidebarHidden ? (
        <AdminSidebarOpener onOpen={() => setSidebarHidden(false)} />
      ) : (
        <AdminSidebar permissions={permissions} onHide={() => setSidebarHidden(true)} />
      )}
      <div className="admin-shell__body">
        <header className="admin-shell__header">
          <div className="admin-shell__brand">
            <h1>{title ?? 'Sense AI — Administration'}</h1>
            <p>{subtitle ?? session?.user.email ?? 'Administration'}</p>
          </div>
          <div className="admin-shell__actions">
            <button className="secondary-button" type="button" onClick={() => navigate('/welcome')}>
              Back to chooser
            </button>
            <button className="ghost-button" type="button" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </header>
        <main className="admin-shell__main">{children}</main>
      </div>
    </div>
  );
}
